import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { apiData } from "../services/api_service";
import { formatAED } from "../utils/format";
import { creditApprovalBaseAPI } from "../config";

// Everything one customer has not paid yet — unpaid invoices (credit ones
// included), and CDNs / Proformas not yet invoiced that still have a balance.
// Opened from the "Unpaid Balance" column on the Credit Approvals list.
function Section({ title, rows, total, empty, onViewAll }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 0.75 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
          {title}
          <Typography component="span" sx={{ fontWeight: 400, fontSize: 12.5, color: "text.secondary", ml: 1 }}>
            {rows.length} · {formatAED(total)}
          </Typography>
        </Typography>
        {onViewAll && rows.length > 0 && (
          <Button size="small" sx={{ textTransform: "none", fontSize: 12.5 }} onClick={onViewAll}>
            Open in list
          </Button>
        )}
      </Box>
      <Box sx={{ border: "1px solid #E3E6EA", borderRadius: "8px", overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { fontSize: 12, fontWeight: 700, bgcolor: "#F8FAFC" } }}>
              <TableCell>Number</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Paid</TableCell>
              <TableCell align="right">Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ "& td": { fontSize: 13 } }}>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: "text.secondary", py: 2 }}>
                  {empty}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => {
                const overdue = r.due_date && dayjs(r.due_date).isBefore(dayjs(), "day");
                return (
                  <TableRow key={r.id}>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                      {r.number}
                      {r.payment_kind && (
                        <Typography sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary", lineHeight: 1.2 }}>
                          {r.payment_kind === "credit"
                            ? `Credit${r.credit_days ? ` · ${r.credit_days} days` : ""}`
                            : "Immediate payment"}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {r.order_date ? dayjs(r.order_date).format("DD-MM-YYYY") : "-"}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", color: overdue ? "#B42318" : "inherit", fontWeight: overdue ? 700 : 400 }}>
                      {r.due_date ? dayjs(r.due_date).format("DD-MM-YYYY") : "-"}
                      {overdue && (
                        <Typography component="span" sx={{ ml: 0.75, fontSize: 11 }}>
                          overdue
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{formatAED(r.grand_total)}</TableCell>
                    <TableCell align="right" sx={{ color: r.paid > 0 ? "#047857" : "text.disabled" }}>
                      {r.paid > 0 ? formatAED(r.paid) : "-"}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatAED(r.balance)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

export default function CustomerUnpaidDialog({ open, onClose, customerId, excludeCdnId, onViewCdns, onViewProformas }) {
  const [state, setState] = useState({ loading: false, data: null, error: "" });

  useEffect(() => {
    if (!open || !customerId) return;
    let cancelled = false;
    setState({ loading: true, data: null, error: "" });
    const qs = excludeCdnId ? `?exclude_cdn_id=${excludeCdnId}` : "";
    apiData("get", `${creditApprovalBaseAPI}/customer/${customerId}/unpaid${qs}`)
      .then((res) => {
        if (cancelled) return;
        setState(
          res?.success
            ? { loading: false, data: res.data, error: "" }
            : { loading: false, data: null, error: res?.msg || res?.message || "Couldn't load the unpaid balance." }
        );
      })
      .catch(() => !cancelled && setState({ loading: false, data: null, error: "Couldn't load the unpaid balance." }));
    return () => {
      cancelled = true;
    };
  }, [open, customerId, excludeCdnId]);

  const d = state.data;
  const name = d ? d.customer.company_name || d.customer.name : "";
  const tiles = d
    ? [
        { label: "Total Unpaid", value: d.totals.all, color: d.totals.all > 0 ? "#B45309" : "#047857" },
        { label: "Invoices", value: d.totals.invoices, color: "text.primary" },
        { label: "CDNs", value: d.totals.cdns, color: "text.primary" },
        { label: "Proformas", value: d.totals.proformas, color: "text.primary" },
      ]
    : [];

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Unpaid Balance</Typography>
          {name && (
            <Typography variant="body2" color="text.secondary">
              {name}
              {d?.customer.company_name && d.customer.name ? ` · ${d.customer.name}` : ""}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {state.loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : !d ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            {state.error || "Nothing to show."}
          </Typography>
        ) : (
          <>
            <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
              {tiles.map((t) => (
                <Grid item xs={6} sm={3} key={t.label}>
                  <Box sx={{ border: "1px solid #E3E6EA", borderRadius: "8px", px: 1.5, py: 1 }}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{t.label}</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: t.color }}>
                      {formatAED(t.value)}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Section
              title="Unpaid invoices"
              rows={d.invoices}
              total={d.totals.invoices}
              empty="No unpaid invoices."
            />
            <Section
              title="CDNs not yet invoiced"
              rows={d.cdns}
              total={d.totals.cdns}
              empty="No open CDNs with a balance."
              onViewAll={onViewCdns}
            />
            <Section
              title="Proformas not yet invoiced"
              rows={d.proformas}
              total={d.totals.proformas}
              empty="No open Proformas with a balance."
              onViewAll={onViewProformas}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
